import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWorkout } from '../context/useWorkout';
import { soundManager } from '../utils/SoundManager';

// Import all History trainer data
import historyOneTrainersData from '../data/HistoryOne';
import historyTwoTrainersData from '../data/HistoryTwo';
import historyThreeTrainersData from '../data/HistoryThree';
import historyFourTrainersData from '../data/HistoryFour';
import historyFiveTrainersData from '../data/HistoryFive';

// Import History-specific CSS
import './HistoryOne.css';

const HistoryTrainerPage = () => {
    const { trainerId, programId } = useParams();
    const navigate = useNavigate();
    const { actions } = useWorkout();

    const [trainer, setTrainer] = useState(null);
    const [selectedProgram, setSelectedProgram] = useState(null);
    const [showIntensitySelection, setShowIntensitySelection] = useState(false);
    const [currentRound, setCurrentRound] = useState(1);

    // Combine all History trainer data
    const allHistoryTrainers = {
        ...historyOneTrainersData,
        ...historyTwoTrainersData,
        ...historyThreeTrainersData,
        ...historyFourTrainersData,
        ...historyFiveTrainersData
    };

    useEffect(() => {
        console.log('🏛️ History Trainer Page - Looking for trainer:', trainerId);
        console.log('🔍 Available trainers:', Object.keys(allHistoryTrainers));

        const foundTrainer = allHistoryTrainers[trainerId];
        if (foundTrainer) {
            console.log('✅ Found History trainer:', foundTrainer.name);
            setTrainer(foundTrainer);

            // If programId is provided, find and select that program
            if (programId) {
                const program = foundTrainer.programs.find(p => p.id === programId);
                if (program) {
                    console.log('🎯 Found program:', program.name);
                    setSelectedProgram(program);
                    setShowIntensitySelection(true);
                }
            }
        } else {
            console.error('❌ History trainer not found:', trainerId);
        }

        // Initialize or get current round from localStorage
        const savedRound = localStorage.getItem('currentGameRound');
        if (savedRound) {
            setCurrentRound(parseInt(savedRound, 10));
        }
    }, [trainerId, programId]);

    console.log('🔍 History Debug state:', {
        trainerId,
        programId,
        selectedProgram: selectedProgram?.name,
        showIntensitySelection,
        currentRound
    });

    // Handle program selection
    const handleProgramSelect = (program) => {
        console.log('🎯 History Program selected:', program.name);

        // 🎵 PROGRAM SELECTION SOUND
        soundManager.playButtonSound();

        setSelectedProgram(program);
        setShowIntensitySelection(true);

        // Update URL to include program selection
        navigate(`/history-trainer/${trainerId}/${program.id}/intensity-selection`);
    };

    // Handle intensity selection (start workout)
    const handleIntensitySelect = (intensity) => {
        console.log('🔥 History Intensity selected:', intensity);

        // 🎵 INTENSITY SELECTION SOUND
        soundManager.playSound('next22', 0.8);

        if (!selectedProgram) {
            console.error('❌ No program selected');
            return;
        }

        // Determine reps and sets based on intensity and program defaults
        let reps, sets;
        switch(intensity) {
            case 'light':
                reps = Math.max(Math.floor(selectedProgram.reps * 0.7), 3);
                sets = Math.max(Math.floor(selectedProgram.sets * 0.7), 2);
                break;
            case 'moderate':
                reps = selectedProgram.reps;
                sets = selectedProgram.sets;
                break;
            case 'intense':
                reps = Math.floor(selectedProgram.reps * 1.3);
                sets = Math.floor(selectedProgram.sets * 1.2);
                break;
            case 'game':
                // GAME MODE: Progressive reps based on current round
                reps = currentRound; // Round 1 = 1 rep, Round 2 = 2 reps, etc.
                sets = 1; // Always 1 set in game mode
                console.log(`🎮 GAME MODE: Round ${currentRound} = ${reps} reps`);
                break;
            default:
                reps = selectedProgram.reps;
                sets = selectedProgram.sets;
        }

        console.log(`🏛️ Starting History trainer workout: ${reps} reps × ${sets} sets`);

        // Calculate intensity percentage
        let intensityLevel;
        switch(intensity) {
            case 'light':
                intensityLevel = 70;
                break;
            case 'moderate':
                intensityLevel = 100;
                break;
            case 'intense':
                intensityLevel = 130;
                break;
            case 'game':
                intensityLevel = 100; // Base level for game mode
                break;
            default:
                intensityLevel = 100;
        }

        // Calculate workout metrics
        const exerciseCount = selectedProgram.pattern.length;
        const totalReps = reps * exerciseCount * sets;

        console.log(`🏛️ Workout metrics: ${exerciseCount} exercises, ${totalReps} total reps`);

        // Store History trainer info in sessionStorage (same as Japan flow)
        const trainerInfo = {
            isTrainerProgram: true,
            isHistoryTrainer: true,
            category: 'history-trainer',
            workoutType: 'history-trainer',
            trainerName: trainer.name,
            programName: selectedProgram.name,
            intensityLevel: intensityLevel,
            intensityLabel: intensity === 'game' ? `Game Mode - Round ${currentRound}` : intensity.charAt(0).toUpperCase() + intensity.slice(1),
            trainerId: trainer.id,
            programId: selectedProgram.id,
            repCount: reps,
            setCount: sets,
            totalReps: totalReps,
            exerciseCount: exerciseCount,
            isGameMode: intensity === 'game',
            currentRound: intensity === 'game' ? currentRound : null
        };

        console.log('🏛️ Storing History trainer info:', trainerInfo);
        sessionStorage.setItem('trainerInfo', JSON.stringify(trainerInfo));

        // Set workout context
        actions.setMuscleGroups(selectedProgram.pattern);

        // Navigate to WorkoutSummary for email collection (same as Japan flow)
        navigate('/workout-summary', {
            state: {
                trainerId: trainer.id,
                trainerName: trainer.name,
                programId: selectedProgram.id,
                programName: selectedProgram.name,
                category: 'history-trainer',
                workoutType: 'history-trainer',
                isHistoryTrainer: true,
                isTrainerProgram: true,
                workoutCode: selectedProgram.pattern,
                reps: reps,
                sets: sets,
                repCount: reps,
                setCount: sets,
                totalReps: totalReps,
                exerciseCount: exerciseCount,
                intensity: intensity,
                intensityLabel: intensity === 'game' ? `Game Mode - Round ${currentRound}` : intensity.charAt(0).toUpperCase() + intensity.slice(1),
                intensityLevel: intensityLevel,
                isGameMode: intensity === 'game',
                currentRound: intensity === 'game' ? currentRound : null
            }
        });
    };

    // Handle advancing to next round (for game mode)
    const handleNextRound = () => {
        const nextRound = currentRound + 1;
        setCurrentRound(nextRound);
        localStorage.setItem('currentGameRound', nextRound.toString());
        console.log(`🎮 Advanced to Round ${nextRound}`);
    };

    // Handle resetting game mode
    const handleResetGame = () => {
        setCurrentRound(1);
        localStorage.setItem('currentGameRound', '1');
        console.log('🎮 Game reset to Round 1');
    };

    // Handle back navigation
    const handleBackToCategories = () => {
        soundManager.playButtonSound();
        navigate('/history-train-categories');
    };

    const handleBackToTrainer = () => {
        soundManager.playButtonSound();
        setShowIntensitySelection(false);
        setSelectedProgram(null);
        navigate(`/history-trainer/${trainerId}`);
    };

    if (!trainer) {
        return (
            <div className="history-trainers-container">
                <div className="history-animated-background"></div>
                <div className="history-trainers-header">
                    <h1>History Trainer Not Found</h1>
                    <button
                        className="back-to-categories-button"
                        onClick={handleBackToCategories}
                    >
                        ← Back to Categories
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="history-trainers-container">
            <div className="history-animated-background"></div>

            {/* Back Button */}
            <button
                className="back-to-categories-button"
                onClick={showIntensitySelection ? handleBackToTrainer : handleBackToCategories}
            >
                ← {showIntensitySelection ? 'Back to Trainer' : 'Back to Categories'}
            </button>

            {!showIntensitySelection ? (
                // Trainer Profile View
                <div className="trainer-profile-section">
                    <div className="history-trainers-header">
                        <div className="header-content">
                            <h1 style={{ color: trainer.color }}>{trainer.name}</h1>
                            <p className="trainer-specialization">{trainer.specialization}</p>
                            <div className="category-stats">
                                <span className="stat-badge">Experience: {trainer.experience}</span>
                                <span className="stat-badge">Programs: {trainer.programs.length}</span>
                                <span className="stat-badge">Category: Historical Legend</span>
                            </div>
                        </div>
                    </div>

                    {/* Trainer Image */}
                    <div className="history-trainer-image-section">
                        <div
                            className="history-trainer-image"
                            style={{
                                backgroundImage: `url(${trainer.image})`,
                                '--history-primary-color': trainer.color,
                                '--history-secondary-color': trainer.secondaryColor
                            }}
                        >
                            <div className="empire-glow-effect"></div>
                            <div className="experience-badge history-badge">
                                {trainer.experience}
                            </div>
                        </div>
                    </div>

                    {/* Trainer Introduction */}
                    <div className="trainer-intro-section">
                        <div className="empire-divider"></div>
                        <p className="trainer-intro-text">{trainer.intro}</p>
                        <div className="empire-divider"></div>
                    </div>

                    {/* Programs Section */}
                    <div className="programs-section">
                        <h2>Training Programs</h2>
                        <div className="programs-grid">
                            {trainer.programs.map((program, index) => (
                                <div
                                    key={program.id}
                                    className="program-card"
                                    onClick={() => handleProgramSelect(program)}
                                    style={{
                                        '--history-primary-color': trainer.color,
                                        '--history-secondary-color': trainer.secondaryColor
                                    }}
                                >
                                    <div className="crown-accent">👑</div>
                                    <h3 className="program-name">{program.name}</h3>
                                    <p className="program-description">{program.description}</p>

                                    <div className="program-stats">
                                        <span className="program-stat">
                                            <strong>Reps:</strong> {program.reps}
                                        </span>
                                        <span className="program-stat">
                                            <strong>Sets:</strong> {program.sets}
                                        </span>
                                        <span className="program-focus">
                                            {program.focus}
                                        </span>
                                    </div>

                                    <button
                                        className="select-history-trainer-button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleProgramSelect(program);
                                        }}
                                        style={{
                                            '--history-primary-color': trainer.color,
                                            '--history-secondary-color': trainer.secondaryColor,
                                            marginTop: '15px',
                                            width: '100%'
                                        }}
                                    >
                                        Start Training
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                // Intensity Selection View
                <div className="intensity-selection-section">
                    <div className="history-trainers-header">
                        <div className="header-content">
                            <h1 style={{ color: trainer.color }}>{selectedProgram.name}</h1>
                            <p>with {trainer.name}</p>
                            <p className="program-description">{selectedProgram.description}</p>
                        </div>
                    </div>

                    {/* Game Mode Round Display */}
                    <div className="game-mode-status">
                        <div className="current-round-display">
                            <h3>🎮 Current Game Round: {currentRound}</h3>
                            <div className="round-controls">
                                <button className="round-button" onClick={handleNextRound}>
                                    Advance to Round {currentRound + 1}
                                </button>
                                <button className="round-button reset" onClick={handleResetGame}>
                                    Reset to Round 1
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="intensity-selection-grid">
                        <div
                            className="intensity-card light"
                            onClick={() => handleIntensitySelect('light')}
                        >
                            <h3>🌟 Light Training</h3>
                            <p>Perfect for beginners or recovery days</p>
                            <div className="intensity-stats">
                                <span>Reps: {Math.max(Math.floor(selectedProgram.reps * 0.7), 3)}</span>
                                <span>Sets: {Math.max(Math.floor(selectedProgram.sets * 0.7), 2)}</span>
                            </div>
                        </div>

                        <div
                            className="intensity-card moderate"
                            onClick={() => handleIntensitySelect('moderate')}
                        >
                            <h3>⚔️ Moderate Training</h3>
                            <p>Standard training as designed by the legend</p>
                            <div className="intensity-stats">
                                <span>Reps: {selectedProgram.reps}</span>
                                <span>Sets: {selectedProgram.sets}</span>
                            </div>
                        </div>

                        <div
                            className="intensity-card intense"
                            onClick={() => handleIntensitySelect('intense')}
                        >
                            <h3>🔥 Intense Training</h3>
                            <p>For experienced warriors seeking ultimate challenge</p>
                            <div className="intensity-stats">
                                <span>Reps: {Math.floor(selectedProgram.reps * 1.3)}</span>
                                <span>Sets: {Math.floor(selectedProgram.sets * 1.2)}</span>
                            </div>
                        </div>

                        <div
                            className="intensity-card game-mode"
                            onClick={() => handleIntensitySelect('game')}
                        >
                            <h3>✨ GAME MODE ✨</h3>
                            <p>Progressive card game: Round {currentRound} = {currentRound} {currentRound === 1 ? 'rep' : 'reps'} per exercise</p>
                            <div className="intensity-stats">
                                <span>Current Round: {currentRound}</span>
                                <span>Reps This Round: {currentRound}</span>
                                <span>Sets: 1</span>
                            </div>
                            <div className="game-mode-preview">
                                <small>Next round will be {currentRound + 1} reps per exercise</small>
                            </div>
                        </div>
                    </div>

                    <div className="selected-program-info">
                        <h3>Program Focus: {selectedProgram.focus}</h3>
                        <p><strong>Training Pattern:</strong> {selectedProgram.pattern}</p>
                    </div>
                </div>
            )}

            {/* Footer */}
            <div className="history-trainers-footer">
                <div className="history-info">
                    <h3>Historical Training Philosophy</h3>
                    <div className="history-benefits-grid">
                        <div className="history-benefit-item">
                            <div className="benefit-icon">🏛️</div>
                            <div>
                                <strong>Ancient Wisdom</strong>
                                <p>Training methods refined by history's greatest figures across all civilizations</p>
                            </div>
                        </div>
                        <div className="history-benefit-item">
                            <div className="benefit-icon">👑</div>
                            <div>
                                <strong>Legendary Power</strong>
                                <p>Develop the strength and character that built empires and changed the world</p>
                            </div>
                        </div>
                        <div className="history-benefit-item">
                            <div className="benefit-icon">⚔️</div>
                            <div>
                                <strong>Conquest Mentality</strong>
                                <p>Achieve the mindset that overcame impossible odds and achieved greatness</p>
                            </div>
                        </div>
                        <div className="history-benefit-item">
                            <div className="benefit-icon">🌍</div>
                            <div>
                                <strong>Global Heritage</strong>
                                <p>Connect with training wisdom from every corner of human civilization</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HistoryTrainerPage;